/* video-pause.js
   ---------------------------------------------------------------------------
   WCAG 2.2.2 (Pause, Stop, Hide, Level A): any motion that starts automatically
   and runs longer than five seconds must give the user a way to stop it. Every
   auto-playing video on this site loops, so every one of them qualifies.

   The PDP videos are deliberately chrome-less (`controlsList`,
   `disablePictureInPicture`, Vimeo `?background=1`), so rather than turning on
   the native control bar this injects one small branded pause toggle per video.

   Handles both shapes used in the theme:
     - <video autoplay loop ...>            -> .pause() / .play()
     - Vimeo <iframe ?background=1>          -> player postMessage API, which
       works on background embeds without loading the Vimeo Player SDK.

   Site-wide by design. The seven main-product-* sections, background-video,
   slideshow, and video all route through here, so the criterion is satisfied
   in one place instead of nine. See ACCESSIBILITY.md Hard Rule 9.
   --------------------------------------------------------------------------- */
(function () {
  'use strict';

  var STYLE_ID = 'giraux-video-pause-style';
  var CSS = [
    '.gvp__btn{',
    'position:absolute;bottom:12px;right:12px;z-index:5;',
    'width:40px;height:40px;padding:0;border:0;border-radius:50%;',
    'display:flex;align-items:center;justify-content:center;',
    'background:rgba(26,19,16,0.62);color:#fff;cursor:pointer;',
    'backdrop-filter:blur(4px);-webkit-backdrop-filter:blur(4px);',
    'transition:background 0.2s ease,opacity 0.2s ease;opacity:0.75;',
    '}',
    '.gvp__btn:hover{background:rgba(26,19,16,0.85);opacity:1;}',
    /* Focus ring is ink-on-white safe at 3:1 and does not rely on the gold. */
    '.gvp__btn:focus-visible{outline:2px solid #FFFFFF;outline-offset:2px;opacity:1;}',
    '.gvp__btn svg{width:16px;height:16px;display:block;fill:currentColor;pointer-events:none;}',
    '@media (prefers-reduced-motion: reduce){.gvp__btn{transition:none;}}'
  ].join('');

  var PAUSE_ICON = '<svg viewBox="0 0 24 24" aria-hidden="true"><rect x="6" y="4" width="4" height="16" rx="1"/><rect x="14" y="4" width="4" height="16" rx="1"/></svg>';
  var PLAY_ICON = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M8 5v14l11-7z"/></svg>';

  function injectStyle() {
    if (document.getElementById(STYLE_ID)) return;
    var s = document.createElement('style');
    s.id = STYLE_ID;
    s.textContent = CSS;
    document.head.appendChild(s);
  }

  function reduceMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /* Vimeo background embeds accept the player postMessage API directly. */
  function vimeoCmd(iframe, method) {
    try {
      iframe.contentWindow.postMessage(JSON.stringify({ method: method }), '*');
    } catch (e) { /* cross-origin not ready yet; the button state still flips */ }
  }

  function addButton(media, isVimeo) {
    var host = media.parentElement;
    if (!host || host.querySelector('.gvp__btn')) return;

    /* The button is absolutely positioned, so the host must establish a
       containing block. Every current host is already position:relative, but a
       future section might not be. */
    if (getComputedStyle(host).position === 'static') host.style.position = 'relative';

    var playing = true;
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'gvp__btn';

    function render() {
      btn.innerHTML = playing ? PAUSE_ICON : PLAY_ICON;
      btn.setAttribute('aria-label', playing ? 'Pause video' : 'Play video');
    }

    function setPlaying(next) {
      playing = next;
      if (isVimeo) {
        vimeoCmd(media, playing ? 'play' : 'pause');
      } else if (playing) {
        var p = media.play();
        if (p && p.catch) p.catch(function () {});
      } else {
        media.pause();
      }
      render();
    }

    btn.addEventListener('click', function () { setPlaying(!playing); });

    /* Native <video> can also be paused by other code or by the OS; keep the
       button honest rather than assuming our click is the only source. */
    if (!isVimeo) {
      media.addEventListener('play', function () { playing = true; render(); });
      media.addEventListener('pause', function () { playing = false; render(); });
    }

    render();
    host.appendChild(btn);

    /* Reduced motion is not sufficient on its own to satisfy 2.2.2, which is
       why the button exists, but honoring it is still the right default. */
    if (reduceMotion()) setPlaying(false);
  }

  function scan() {
    injectStyle();

    var videos = document.querySelectorAll('video[autoplay]:not([data-gvp])');
    for (var i = 0; i < videos.length; i++) {
      videos[i].setAttribute('data-gvp', '');
      addButton(videos[i], false);
    }

    var frames = document.querySelectorAll('iframe[src*="player.vimeo.com"]:not([data-gvp])');
    for (var j = 0; j < frames.length; j++) {
      /* Only background embeds lack their own controls. A normal Vimeo embed
         already ships a player bar and needs nothing from us. */
      if (frames[j].src.indexOf('background=1') === -1) continue;
      frames[j].setAttribute('data-gvp', '');
      addButton(frames[j], true);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scan);
  } else {
    scan();
  }

  /* PDP galleries and search-pro grids swap media in after load. */
  if (window.MutationObserver) {
    var pending = null;
    new MutationObserver(function () {
      clearTimeout(pending);
      pending = setTimeout(scan, 150);
    }).observe(document.documentElement, { childList: true, subtree: true });
  }
})();
